import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import { roleFromApi, roleToApi, type MemberRole } from '@/core/constants/roles';
import type {
  ProjectMember,
  ProjectMemberDto,
  ProjectMemberDtoPagedResult,
  AddProjectMemberPayload,
  UpdateProjectMemberPayload,
} from '../types';

export interface ProjectMembersQueryParams {
  Page?: number;
  PageSize?: number;
  SearchTerm?: string;
  SortBy?: string;
  SortAscending?: boolean;
}

export interface ProjectMembersSearchResult {
  items: ProjectMember[];
  totalCount: number;
}

function adaptMember(dto: ProjectMemberDto, projectId: string): ProjectMember {
  return {
    id: dto.id ?? '',
    projectId,
    userId: dto.user.userId ?? '',
    displayName: dto.user.displayName ?? dto.user.email ?? '',
    email: dto.user.email ?? '',
    avatarUrl: dto.user.avatarUrl ?? null,
    role: roleFromApi(dto.role),
    createdAt: dto.createdAt,
  };
}

export const projectMembersService = {
  /**
   * Returns all members of a project.
   * GET /api/projects/{projectId}/members
   */
  getList: async (
    projectId: string,
    params?: ProjectMembersQueryParams,
    signal?: AbortSignal,
  ): Promise<ProjectMember[]> => {
    const { data } = await httpClient.get<ProjectMemberDtoPagedResult>(
      `${API_V1}/projects/${projectId}/members`,
      { params, signal },
    );
    return (data.items ?? []).map((dto) => adaptMember(dto, projectId));
  },

  /**
   * Returns a searchable, paginated slice of a project's members — used by
   * pickers where the list can't be assumed to be small (e.g. the task
   * assignee picker).
   * GET /api/projects/{projectId}/members
   */
  search: async (
    projectId: string,
    params: ProjectMembersQueryParams,
    signal?: AbortSignal,
  ): Promise<ProjectMembersSearchResult> => {
    const { data } = await httpClient.get<ProjectMemberDtoPagedResult>(
      `${API_V1}/projects/${projectId}/members`,
      { params, signal },
    );
    return {
      items: (data.items ?? []).map((dto) => adaptMember(dto, projectId)),
      totalCount: data.totalCount,
    };
  },

  /**
   * Adds a member to a project.
   * POST /api/projects/{projectId}/members
   */
  add: async (projectId: string, payload: AddProjectMemberPayload): Promise<void> => {
    await httpClient.post<AddProjectMemberPayload, void>(
      `${API_V1}/projects/${projectId}/members`,
      { ...payload, role: roleToApi(payload.role as MemberRole) },
    );
  },

  /**
   * Updates a member's role in a project.
   * PUT /api/projects/{projectId}/members/{memberId}
   */
  update: async (
    projectId: string,
    memberId: string,
    payload: UpdateProjectMemberPayload,
  ): Promise<void> => {
    await httpClient.put<UpdateProjectMemberPayload, void>(
      `${API_V1}/projects/${projectId}/members/${memberId}`,
      { ...payload, role: roleToApi(payload.role as MemberRole) },
    );
  },

  /**
   * Removes a member from a project.
   * DELETE /api/projects/{projectId}/members/{memberId}
   */
  remove: async (projectId: string, memberId: string): Promise<void> => {
    await httpClient.delete(`${API_V1}/projects/${projectId}/members/${memberId}`);
  },
};
